import { CardNumberElement } from "@stripe/react-stripe-js";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid';
import { FIREBASE_APP } from "../Firebase";
import { getRemoteEnv } from '../config/remoteEnv';
import { httpsCallableWithAppId } from "./CallableWrapper";
import { cartHasRewards, getLoyaltyToken } from "./LoyaltyService";

// PPB rejects payments with a specific reason (loyalty balance, reward
// validity, minimum spend…) — surface that text instead of a generic
// failure. Firebase prefixes messages with the error code sometimes.
const cleanPpbMessage = (raw: any): string | undefined => {
    const msg = typeof raw === 'string' ? raw : raw?.message;
    if (!msg || typeof msg !== 'string') return undefined;
    return msg.replace(/^(functions\/)?[a-z-]+:\s*/i, '').replace(/^ERROR:\s*/i, '').trim() || undefined;
};


class StripeService {

    private static generateRandomId() {
        // Generate two random letters
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const randomLetters = 
            letters.charAt(Math.floor(Math.random() * letters.length)) +
            letters.charAt(Math.floor(Math.random() * letters.length));
        
        // Generate two random digits
        const randomDigits = 
            Math.floor(Math.random() * 10) + 
            '' + 
            Math.floor(Math.random() * 10);
    
        // Combine letters and digits
        return randomLetters + randomDigits;
    }

    public static expressCheckout = async (stripe: any, paymentMethodId: string, cart: any, total: number, tipAmount: number, tableNumber: number | null, formData: any, order_type: string, delivery_address: any, delivery_quote: any, test_mode: boolean, restaurantId: string, agreeToMarketing: boolean, marketingRefused: boolean = false) => {
        interface PaymentResponse {
            clientSecret: string;
            paymentIntentId: string;
        }

        const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region);

        if (!stripe) {
        return {status: "FAILURE"};
        }
        
        console.log("create payment intent")

        try {
            console.log("EMAIL ADDRESS IS:", formData.email);

        // Reward lines are paid in points — PPB validates them against the
        // signed-in customer's balance, authenticated by their own token.
        let loyaltyToken: string | null = null;
        if (cartHasRewards(cart)) {
            loyaltyToken = await getLoyaltyToken();
            if (!loyaltyToken) return { status: "FAILURE", message: "Please sign in to redeem your rewards." };
        }

        const processPayment = httpsCallableWithAppId(functions, 'PPB');
        const orderId = uuidv4();
        const orderCode = this.generateRandomId();

            return processPayment({ order_id: orderId, order_code: orderCode, restaurant_name: restaurantId, store_id: restaurantId, cart: cart, amount: total, tipAmount: tipAmount, tableNumber: order_type==="dinein" ? tableNumber : null, currency: getRemoteEnv().currencyCode, name: formData.name, cardholderName: formData.name, email: formData.email, phone: formData.phone, payment_method_id: paymentMethodId, test_mode: test_mode, order_type: order_type, delivery_address: order_type === "delivery" ? delivery_address : null, delivery_quote: order_type === "delivery" ? delivery_quote : null, agreeToMarketing: agreeToMarketing, marketingRefused: marketingRefused, ...(loyaltyToken ? { loyalty_token: loyaltyToken } : {})}).then(response => {
                const clientSecret = (response.data as PaymentResponse).clientSecret;
                console.log("successful response received")

                return stripe?.confirmCardPayment(clientSecret).then(
                    (result: any) => {
                        console.log(result)
                    if (result.error) {
                        console.log(`Payment failed: ${result.error.message}`);
                        return {status: "FAILURE", message: result.error.message}
                    } else if (result.paymentIntent?.status === 'succeeded') {
                        return {status: "SUCCESS", orderCode: orderCode, orderId: orderId};
                    }
                    return {status: "FAILURE"}
                }).catch((error: any) => {
                    console.log(error)
                    return {status: "FAILURE"}
                })


            }).catch((error: any) => {
                console.log("error PPB: ", JSON.stringify(error))

                // Firebase Functions errors have the message in error.message
                const errorMessage = error.message || error.toString();

                if (errorMessage.includes("Delivery quote has expired")) {
                    return {status: "QUOTE_EXPIRED"}
                }

                return {status: "FAILURE", message: cleanPpbMessage(errorMessage)}
            })
                

        } catch (error) {
            console.log(`Payment failed: ${(error as Error).message}`);
            return {status: "FAILURE"}
        }

    };

    public static createPaymentIntent = async (stripe: any, elements: any, cart: any, total: number, tipAmount: number, tableNumber: number | null, formData: any, cardholderName: string, address: any, order_type: string, delivery_address: any, delivery_quote: any, test_mode: boolean, restaurantId: string, agreeToMarketing: boolean, marketingRefused: boolean = false) => {
        interface PaymentResponse {
            clientSecret: string;
            paymentIntentId: string;
        }

        const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region)

        if (!stripe || !elements) {
        return {status: "FAILURE"};
        }
        
        const cardElement = elements.getElement(CardNumberElement)

        try {
            console.log("EMAIL ADDRESS IS:", formData.email);
            console.log("ADDRESS IS: ", address)

            const checkTime = httpsCallableWithAppId(functions, 'getLocalTimeObject')
            checkTime();

        // Reward lines are paid in points — PPB validates them against the
        // signed-in customer's balance, authenticated by their own token.
        let loyaltyToken: string | null = null;
        if (cartHasRewards(cart)) {
            loyaltyToken = await getLoyaltyToken();
            if (!loyaltyToken) return { status: "FAILURE", message: "Please sign in to redeem your rewards." };
        }

        const processPayment = httpsCallableWithAppId(functions, 'PPB');
        const orderId = uuidv4();
        const orderCode = this.generateRandomId();

            const paymentData = { 
                order_id: orderId, 
                order_code: orderCode, 
                restaurant_name: restaurantId,
                store_id: restaurantId,
                cart: cart, 
                amount: total, 
                tipAmount: tipAmount, 
                tableNumber: order_type==="dinein" ? tableNumber : null, 
                currency: getRemoteEnv().currencyCode, 
                name: formData.name, 
                cardholderName: cardholderName, 
                email: formData.email, 
                phone: formData.phone, 
                order_type: order_type, 
                delivery_address: order_type === "delivery" ? delivery_address : null, 
                delivery_quote: order_type === "delivery" ? delivery_quote : null, 
                test_mode: test_mode,
                agreeToMarketing: agreeToMarketing,
                marketingRefused: marketingRefused,
                ...(loyaltyToken ? { loyalty_token: loyaltyToken } : {})
            };

            console.log("PAYMENT DATA BEING SENT:", paymentData);

        console.log("PPB request: ", { order_id: orderId, order_code: orderCode, restaurant_name: restaurantId, cart: cart, amount: total, tipAmount: tipAmount, tableNumber: order_type==="dinein" ? tableNumber : null, currency: getRemoteEnv().currencyCode, name: formData.name, cardholderName: cardholderName, email: formData.email, phone: formData.phone, order_type: order_type, delivery_address: order_type === "delivery" ? delivery_address : null, delivery_quote: order_type === "delivery" ? delivery_quote : null, test_mode: test_mode})

            return processPayment(paymentData).then(response => {
                const clientSecret = (response.data as PaymentResponse).clientSecret;
                console.log("successful response received")

                return stripe?.confirmCardPayment(clientSecret, {
                    payment_method: { 
                        card: cardElement,
                        billing_details: {
                            name: formData.name,
                            email: formData.email,
                            address: address || undefined,
                        }
                    },
                }).then((result: any) => {
                    console.log(result)
                    if (result.error) {
                        console.log(`Payment failed: ${result.error.message}`);
                        return {status: "FAILURE", message: result.error.message}
                    }
                    else if (result.paymentIntent?.status === 'succeeded') {
                        return {status: "SUCCESS", orderCode: orderCode, orderId: orderId};
                    }
                    return {status: "FAILURE"}
                }).catch((error: any) => {
                    console.log(error)
                    return {status: "FAILURE"}
                })


            }).catch((error: any) => {
                console.log("error PPB: ", JSON.stringify(error))

                // Firebase Functions errors have the message in error.message
                const errorMessage = error.message || error.toString();

                if (errorMessage.includes("Delivery quote has expired")) {
                    return {status: "QUOTE_EXPIRED"}
                }

                return {status: "FAILURE", message: cleanPpbMessage(errorMessage)}
            })
                

        } catch (error) {
            console.log(`Payment failed: ${(error as Error).message}`);
            return {status: "FAILURE"}
        }

    };

}

export default StripeService;

